import { makeVar, ReactiveVar, useReactiveVar } from "@apollo/client";
import { API_DATE_FORMAT, formatDateStringForApi, parseDate, readURIParams } from "@flixbus-phx/marketplace-common";
import { add, differenceInCalendarDays, format, isBefore, isFuture, isSameDay, isToday } from "date-fns";
import { useSearchParams } from "react-router-dom";
import addGetParams from "../../shared/helpers/addGetParams/addGetParams";
import isValidDate from "../../shared/helpers/isValidDate/isValidDate";
import { DayOfWeek } from "../../shared/types/schema";
import { paginationVar, useResetPagination } from "../usePagination/usePagination";

export type RideFilter = {
  lineId: string;
  hideCompletedRides: boolean;
  departureStationId: string;
  departureTime: string;
  period: {
    startDate: string;
    endDate: string;
  };
  departureDays: Array<DayOfWeek>;
};

export const getLineUuid = (): RideFilter["lineId"] => {
  const { lineId } = readURIParams(window.location.search);
  return lineId || "";
};

export const getDepartureStationUuid = (): RideFilter["departureStationId"] => {
  const { departureStationId } = readURIParams(window.location.search);
  return departureStationId || "";
};

export const getDepartureTime = (): RideFilter["departureTime"] => {
  const { departureTime } = readURIParams(window.location.search);

  if (departureTime && /^([0-1][0-9]|2[0-3]):([0-5][0-9])$/.test(departureTime)) {
    return departureTime;
  }

  return "";
};

export const getPeriod = (): RideFilter["period"] => {
  const { startDate } = readURIParams(window.location.search);
  const { endDate } = readURIParams(window.location.search);

  if (
    startDate &&
    endDate &&
    isValidDate(startDate) &&
    isValidDate(endDate) &&
    (isBefore(new Date(startDate), new Date(endDate)) || isSameDay(new Date(startDate), new Date(endDate))) &&
    differenceInCalendarDays(new Date(endDate), new Date(startDate)) <= 31
  ) {
    return {
      startDate: formatDateStringForApi(startDate),
      endDate: formatDateStringForApi(endDate),
    };
  }

  return {
    startDate: formatDateStringForApi(format(Date.now(), API_DATE_FORMAT)),
    endDate: formatDateStringForApi(format(add(Date.now(), { days: 6 }), API_DATE_FORMAT)),
  };
};

export const getDepartureDays = (): RideFilter["departureDays"] => {
  const { departureDays } = readURIParams(window.location.search);
  const days: Array<string> = (departureDays || "").split(";");

  return Object.values(DayOfWeek).filter((dow) => days.includes(dow));
};

export const getHideCompletedRidesBasedOnPeriod = (period: RideFilter["period"]): RideFilter["hideCompletedRides"] => {
  const startDate = parseDate(period.startDate);

  // noinspection RedundantIfStatementJS
  if (isToday(startDate) || isFuture(startDate)) {
    return true;
  }

  return false;
};

export const rideFilterVar = makeVar({
  lineId: getLineUuid(),
  hideCompletedRides: getHideCompletedRidesBasedOnPeriod(getPeriod()),
  departureStationId: getDepartureStationUuid(),
  departureTime: getDepartureTime(),
  period: getPeriod(),
  departureDays: getDepartureDays(),
});

export const useResetRideFilter = (reactiveVar: ReactiveVar<RideFilter>) => {
  const rideFilter = useReactiveVar(reactiveVar);
  return () => {
    const defaultValue = {
      period: {
        startDate: formatDateStringForApi(format(Date.now(), API_DATE_FORMAT)),
        endDate: formatDateStringForApi(format(add(Date.now(), { days: 6 }), API_DATE_FORMAT)),
      },
      lineId: "",
      hideCompletedRides: true,
      departureStationId: "",
      departureTime: "",
      departureDays: [],
    };
    if (JSON.stringify(rideFilter) !== JSON.stringify(defaultValue)) {
      reactiveVar(defaultValue);
    }
  };
};

const useRideFilter = (
  reactiveVar: ReactiveVar<RideFilter>
): [RideFilter, (changedRideFilter: RideFilter) => void, (changedPeriod: RideFilter) => void] => {
  const rideFilter = useReactiveVar(reactiveVar);
  const [searchParams, setSearchParams] = useSearchParams();
  const resetPagination = useResetPagination(paginationVar);

  const convertParams = (values: RideFilter) => {
    return {
      lineId: values.lineId,
      departureStationId: values.departureStationId,
      departureTime: values.departureTime,
      departureDays: values.departureDays.join(";"),
      startDate: values.period.startDate,
      endDate: values.period.endDate,
    };
  };

  const setParams = (changedRideFilter: RideFilter) => {
    const newSearchParams = convertParams(changedRideFilter);
    addGetParams(newSearchParams, searchParams);
    setSearchParams(searchParams);
  };

  const setRideFilter = (changedRideFilter: RideFilter) => {
    reactiveVar({
      ...changedRideFilter,
    });
    resetPagination();
    setParams(changedRideFilter);
  };

  const setPeriodAndRideFilter = (changedPeriodAndRideFilter: RideFilter) => {
    const { period } = changedPeriodAndRideFilter;
    reactiveVar({
      ...changedPeriodAndRideFilter,
      hideCompletedRides: getHideCompletedRidesBasedOnPeriod(period),
      period: {
        startDate: period.startDate,
        endDate: period.endDate,
      },
    });
    resetPagination();
    setParams(changedPeriodAndRideFilter);
  };

  return [rideFilter, setRideFilter, setPeriodAndRideFilter];
};

export default useRideFilter;
